import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import BASE_URL from '../../../../config/config';
import i18n from '../../../../i18n';
import { cabecerasAuth, claves } from '../../../../queries/cliente';

// Campos que el formulario puede tocar. El PUT solo envía estos: mandar el
// objeto entero devuelto por la API arrastraba campos que el backend no debe
// recibir (entre ellos la contraseña, que se rehasheaba y dejaba al usuario
// sin acceso).
export const CAMPOS_EDITABLES = [
  'nombre',
  'apellidos',
  'email',
  'telefono',
  'webs_del_cliente',
  'role',
  'estado',
  'dpto_tecnico',
  'emails_cc',
];

const soloEditables = (datos) => CAMPOS_EDITABLES.reduce((acc, campo) => {
  acc[campo] = datos[campo] ?? '';
  return acc;
}, {});

/**
 * Carga y estado del detalle de usuario: datos, tickets, auditoría y servicios.
 * Cada bloque es su propia consulta de react-query: se piden en paralelo, se
 * cachean por separado y un fallo en la auditoría no impide ver la ficha.
 */
const useDetalleUsuario = (usuario) => {
  const token = localStorage.getItem('AuthToken');
  const queryClient = useQueryClient();

  const viewerRole = useMemo(() => {
    try {
      return token ? jwtDecode(token).role : null;
    } catch (err) {
      return null;
    }
  }, [token]);

  const esAdmin = viewerRole === 'admin';

  const [form, setForm] = useState({});
  const [serviciosAsignados, setServiciosAsignados] = useState([]);

  const [consultaUsuario, consultaTickets, consultaAuditoria, consultaServicios, consultaCatalogo] = useQueries({
    queries: [
      {
        queryKey: claves.usuario(usuario),
        queryFn: async () => {
          const resp = await axios.get(`${BASE_URL}/usuario/info/${usuario}`, cabecerasAuth());
          return resp.data.usuarios || resp.data.usuario || resp.data;
        },
        enabled: Boolean(usuario),
      },
      {
        queryKey: claves.ticketsUsuario(usuario),
        queryFn: async () => {
          const resp = await axios.get(`${BASE_URL}/tickets/total/${usuario}`);
          const lista = resp.data.tickets || [];
          return [...lista].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        },
        enabled: Boolean(usuario),
      },
      {
        queryKey: claves.auditoriaUsuario(usuario),
        queryFn: async () => {
          const resp = await axios.get(`${BASE_URL}/usuario/auditoria/${usuario}`, cabecerasAuth());
          return resp.data?.success ? (resp.data.registros || []) : [];
        },
        enabled: Boolean(usuario),
      },
      {
        queryKey: claves.serviciosUsuario(usuario),
        queryFn: async () => {
          const resp = await axios.get(`${BASE_URL}/servicios/usuario/${usuario}`, cabecerasAuth());
          return resp.data?.success ? (resp.data.servicios || []) : [];
        },
        enabled: Boolean(usuario),
      },
      {
        queryKey: claves.servicios(),
        queryFn: async () => {
          const resp = await axios.get(`${BASE_URL}/servicios`, cabecerasAuth());
          return resp.data?.success ? (resp.data.servicios || []) : [];
        },
        // El catálogo completo solo lo necesita quien puede reasignar servicios
        enabled: Boolean(usuario) && esAdmin,
      },
    ],
  });

  const usuarioData = consultaUsuario.data || null;
  const tickets = consultaTickets.data || [];
  const auditoria = consultaAuditoria.data || [];
  const serviciosUsuario = useMemo(() => consultaServicios.data || [], [consultaServicios.data]);
  const catalogoServicios = consultaCatalogo.data || [];

  const serviciosOriginales = useMemo(() => serviciosUsuario.map((s) => s.id), [serviciosUsuario]);

  // El formulario y la selección de servicios son estado de edición: parten de
  // lo que devuelve el servidor y se reinician cuando ese dato cambia.
  useEffect(() => {
    if (usuarioData) setForm(soloEditables(usuarioData));
  }, [usuarioData]);

  useEffect(() => {
    setServiciosAsignados(serviciosOriginales);
  }, [serviciosOriginales]);

  // ── Cambios pendientes ────────────────────────────────────────────────────
  // Se calculan una vez por render y se reutilizan: la barra de guardado, el
  // aviso al salir y los contadores de las pestañas leen de aquí.

  const camposCambiados = useMemo(() => {
    if (!usuarioData) return [];
    return CAMPOS_EDITABLES.filter((campo) => (form[campo] ?? '') !== (usuarioData[campo] ?? ''));
  }, [form, usuarioData]);

  const serviciosCambiados = useMemo(() => {
    if (serviciosAsignados.length !== serviciosOriginales.length) return true;
    const originales = new Set(serviciosOriginales);
    return serviciosAsignados.some((id) => !originales.has(id));
  }, [serviciosAsignados, serviciosOriginales]);

  const totalCambios = camposCambiados.length + (serviciosCambiados ? 1 : 0);
  const hayCambios = totalCambios > 0;

  const actualizarCampo = useCallback((campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }, []);

  const alternarServicio = useCallback((id) => {
    setServiciosAsignados((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }, []);

  const descartarCambios = useCallback(() => {
    if (usuarioData) setForm(soloEditables(usuarioData));
    setServiciosAsignados(serviciosOriginales);
  }, [usuarioData, serviciosOriginales]);

  /**
   * Guarda los campos editables y, si el admin ha tocado los servicios, sus
   * asignaciones. Devuelve true si todo fue bien.
   */
  const guardar = useCallback(async (camposNormalizados = {}) => {
    const payload = soloEditables({ ...form, ...camposNormalizados });

    const resp = await axios.put(`${BASE_URL}/usuario/update/${usuario}`, payload, cabecerasAuth());
    const actualizado = resp.data.user || resp.data.usuario || resp.data;
    queryClient.setQueryData(claves.usuario(usuario), actualizado);

    if (esAdmin && serviciosCambiados) {
      await axios.put(`${BASE_URL}/servicios/usuario/${usuario}`, { servicios: serviciosAsignados }, cabecerasAuth());
      queryClient.setQueryData(
        claves.serviciosUsuario(usuario),
        catalogoServicios.filter((s) => serviciosAsignados.includes(s.id)),
      );
    }

    // La ficha cambia el rol/estado: la auditoría y el listado de usuarios
    // dejan de estar al día, así que se marcan para refrescar.
    queryClient.invalidateQueries({ queryKey: claves.auditoriaUsuario(usuario) });
    queryClient.invalidateQueries({ queryKey: claves.usuarios() });

    return true;
  }, [form, usuario, esAdmin, serviciosCambiados, serviciosAsignados, catalogoServicios, queryClient]);

  /**
   * Acción rápida del hero. Va por su cuenta y no arrastra lo que haya sin
   * guardar en el formulario: solo toca el estado.
   */
  const cambiarEstado = useCallback(async (nuevoEstado) => {
    const resp = await axios.put(
      `${BASE_URL}/usuario/update/${usuario}`,
      { ...soloEditables(usuarioData), estado: nuevoEstado },
      cabecerasAuth(),
    );
    const actualizado = resp.data.user || resp.data.usuario || resp.data;
    queryClient.setQueryData(claves.usuario(usuario), actualizado);
    // El formulario sigue con sus cambios pendientes, pero el estado ya está
    // guardado: se sincroniza para que no aparezca como pendiente.
    setForm((prev) => ({ ...prev, estado: actualizado.estado }));
    queryClient.invalidateQueries({ queryKey: claves.auditoriaUsuario(usuario) });
    queryClient.invalidateQueries({ queryKey: claves.usuarios() });
    return actualizado;
  }, [usuario, usuarioData, queryClient]);

  return {
    token,
    viewerRole,
    esAdmin,

    usuarioData,
    form,
    actualizarCampo,
    cargando: consultaUsuario.isPending,
    error: consultaUsuario.isError ? i18n.t('perfilUsuario.errorCargarUsuario') : null,

    tickets,
    cargandoTickets: consultaTickets.isPending,
    auditoria,
    cargandoAuditoria: consultaAuditoria.isPending,

    catalogoServicios,
    serviciosUsuario,
    serviciosAsignados,
    alternarServicio,
    cargandoServicios: consultaServicios.isPending || (esAdmin && consultaCatalogo.isPending),

    camposCambiados,
    serviciosCambiados,
    totalCambios,
    hayCambios,
    descartarCambios,
    guardar,
    cambiarEstado,
  };
};

export default useDetalleUsuario;
